import { Col, Divider, Form, Input, Row, Select } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import { type BaseOptionType } from "antd/lib/select"
import React from "react"
import { useTranslation } from "react-i18next"

import { CustomerAutoComplete } from "../../../../../components/AutoComplete/CustomerAutoComplete.tsx"
import { PhoneInput } from "../../../../../components/PhoneInputs/PhoneInput.tsx"
import {
  CUSTOMER_FIRST_NAME_LABEL,
  CUSTOMER_FIRST_NAME_PLACEHOLDER
} from "../../../../../constants/customer-name.ts"
import { placeholders } from "../../../../../constants/placeholders"
import { normalizeEmail } from "../../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../../utils/validation/validate-phone.ts"
import { type CustomerSearchResult } from "../../../../CustomersPage/types/customer.types.ts"
import type {
  ProjectFormData,
  ProjectFormDataCustomer
} from "../project-form-data.ts"
import { ResolvedSelect } from "../../../../../components/Select/ResolvedSelect.tsx"

interface CustomerContactSectionProps {
  form: FormInstance<ProjectFormData>
  saving?: boolean
  options: Record<string, BaseOptionType[]>
  onCustomerPicked?: (zip: string | null) => void
}

export function CustomerContactSection({
  form,
  saving,
  options,
  onCustomerPicked
}: CustomerContactSectionProps) {
  const key: keyof ProjectFormData = "customer"

  const { t } = useTranslation()
  const status = Form.useWatch([key, "status"], form)

  const isActiveCustomer = status === "Active"
  const isDisabled = saving || isActiveCustomer

  const onCustomerSelect = (selectedCustomer: CustomerSearchResult) => {
    const customerData: ProjectFormDataCustomer = {
      email: selectedCustomer?.email,
      first_name: selectedCustomer?.first_name || null,
      middle_name: selectedCustomer?.middle_name || null,
      last_name: selectedCustomer?.last_name || null,
      phone: selectedCustomer?.phone || null,
      phone_2: selectedCustomer?.phone_2 || null,
      phone_3: selectedCustomer?.phone_3 || null,
      address: selectedCustomer?.address || null,
      address_2: selectedCustomer?.address_2 || null,
      city: selectedCustomer?.city || null,
      state: selectedCustomer?.state || null,
      zip_code: selectedCustomer?.zip_code || null,
      status: selectedCustomer?.status || null
    }

    form.setFieldsValue({ customer: customerData })
    onCustomerPicked?.(customerData.zip_code ?? null)
  }

  const onCustomerClear = () => {
    form.setFieldValue(key, null)
  }

  return (
    <>
      {/* register in the form */}
      <Form.Item<ProjectFormData> name={[key, "status"]} hidden>
        <input />
      </Form.Item>

      <CustomerAutoComplete
        onSelect={onCustomerSelect}
        onClear={onCustomerClear}
        disabled={saving}
      />
      <Divider plain className={"!text-xs !text-black/45"}>
        {t("tabbedForm.customerSearch.orEnterNew")}
      </Divider>

      {/* Email */}
      <Form.Item<ProjectFormData>
        label="Email"
        name={[key, "email"]}
        rules={[
          {
            required: false,
            message: "Please enter email"
          },
          { type: "email", message: "Please enter valid email" }
        ]}
        dependencies={["preferred_contact_method"]}
        normalize={(value: string) => normalizeEmail(value) || null}
      >
        <Input disabled={isDisabled} placeholder={placeholders.email} />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item<ProjectFormData>
            label={CUSTOMER_FIRST_NAME_LABEL}
            name={[key, "first_name"]}
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Please enter first name"
              }
            ]}
          >
            <Input
              disabled={isDisabled}
              placeholder={CUSTOMER_FIRST_NAME_PLACEHOLDER}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item<ProjectFormData>
            label="Middle Name"
            name={[key, "middle_name"]}
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Please enter middle name"
              }
            ]}
          >
            <Input disabled={isDisabled} placeholder="Enter Middle Name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item<ProjectFormData>
            label="Last Name"
            name={[key, "last_name"]}
            rules={[
              {
                required: false,
                whitespace: true,
                message: "Please enter last name"
              }
            ]}
          >
            <Input disabled={isDisabled} placeholder="Enter Last Name" />
          </Form.Item>
        </Col>
      </Row>

      {/* Phone Numbers */}
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item<ProjectFormData>
            label="Primary Phone"
            name={[key, "phone"]}
            dependencies={[
              "preferred_contact_method",
              [key, "phone_2"],
              [key, "phone_3"]
            ]}
            rules={[
              {
                required: false,
                validator: validatePhone
              }
            ]}
          >
            <PhoneInput disabled={isDisabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item<ProjectFormData>
            label="Secondary Phone"
            name={[key, "phone_2"]}
            rules={[
              {
                required: false,
                validator: validatePhone
              }
            ]}
            dependencies={[
              "preferred_contact_method",
              [key, "phone"],
              [key, "phone_3"]
            ]}
          >
            <PhoneInput disabled={isDisabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item<ProjectFormData>
            label="Alternative Phone"
            name={[key, "phone_3"]}
            dependencies={[
              "preferred_contact_method",
              [key, "phone"],
              [key, "phone_2"]
            ]}
            rules={[
              {
                required: false,
                validator: validatePhone
              }
            ]}
          >
            <PhoneInput disabled={isDisabled} />
          </Form.Item>
        </Col>
      </Row>

      <Col span={24}>
        <Form.Item<ProjectFormData>
          label="Contact Method"
          name={"preferred_contact_method"}
          rules={[
            {
              required: false,
              message: "Please select a contact method"
            }
          ]}
          normalize={(value: string) => value ?? null}
        >
          <ResolvedSelect
            allowClear
            className={"w-full"}
            placeholder={"Select a method"}
            options={options?.["contact_method"]}
          />
        </Form.Item>
      </Col>
    </>
  )
}
